import React, { useMemo } from 'react';
import { useEntities, useCreateEntity, useUpdateEntity, useDeleteEntity } from '../../hooks/useSWR';
import { useDynamicLookups } from '../../hooks/useDynamicLookups';
import { tableConfig } from '../../config/appConfig';
import { filterByListScope } from '../../utils/listScopeFilter';
import { isBadgeCellRenderer } from './splitViewFieldDisplay';
import GenericFormsPage from './GenericFormsPage';

/**
 * Enhanced Generic Module Page - Handles nested lookup objects from API
 * 
 * @param {Object} config - Module configuration (documentConfig, userConfig, etc.)
 */
const GenericModulePage = ({ config }) => {
  // Use SWR hooks for data fetching and CRUD operations
  // Use baseEndpoint directly (same style as complaint module)
  const { data: entities = [], error, mutate } = useEntities(config.api.baseEndpoint);
  const createEntity = useCreateEntity(config.api.baseEndpoint);
  const updateEntity = useUpdateEntity(config.api.baseEndpoint);
  const deleteEntity = useDeleteEntity(config.api.baseEndpoint);

  // Fetch lookup data dynamically based on config - fully dynamic approach  
  const lookupEndpoints = useMemo(() => config.api?.lookupEndpoints || {}, [config.api?.lookupEndpoints]);
  
  // Use custom hook to fetch all lookup data dynamically
  const memoizedLookupQueries = useDynamicLookups(lookupEndpoints);

  const scopedEntities = useMemo(
    () => filterByListScope(entities, config.listScope),
    [entities, config.listScope]
  );

  // Enhance entities data with nested object property access for table display
  const enhancedEntities = useMemo(() => {
    if (!scopedEntities || scopedEntities.length === 0) return [];
    
    return scopedEntities.map(entity => {
      const enhanced = { ...entity };
      
      // Ensure fileAttachments is always available as an array (from backend or empty array)
      if (!enhanced.fileAttachments || !Array.isArray(enhanced.fileAttachments)) {
        enhanced.fileAttachments = Array.isArray(entity.fileAttachments) ? entity.fileAttachments : [];
      }
      
      // Calculate fileAttachmentCount if not provided by backend
      if (enhanced.fileAttachmentCount === undefined || enhanced.fileAttachmentCount === null) {
        // Count non-deleted files
        const activeFiles = enhanced.fileAttachments.filter(file => !file.isDeleted);
        enhanced.fileAttachmentCount = activeFiles.length;
      } else {
        // Ensure it's a number
        enhanced.fileAttachmentCount = parseInt(enhanced.fileAttachmentCount) || 0;
      }
      
      // Add flattened properties for table display based on config displayField
      config.fields?.forEach(field => {
        if (field.table?.displayField) {
          // Check if the displayField value is an object that needs to be converted to string
          const displayFieldValue = entity[field.table.displayField];
          
          // Preserve arrays (for multi-select fields like involvedDepartments, fileAttachments)
          if (Array.isArray(displayFieldValue)) {
            enhanced[field.table.displayField] = displayFieldValue;
          } else if (!displayFieldValue && field.table.displayField === 'fileAttachments') {
            // Ensure fileAttachments is always an array (even if undefined/null from backend)
            enhanced[field.table.displayField] = enhanced.fileAttachments || [];
          } else if (displayFieldValue && typeof displayFieldValue === 'object') {
            // Keep full relation objects for badge renderers (split view colors + table badges)
            if (isBadgeCellRenderer(field.table?.cellRenderer)) {
              enhanced[field.table.displayField] = displayFieldValue;
            } else {
            // The displayField points to an object - extract the display value
            // Check fieldMappings first for specific field mappings (e.g., riskAssessment.code, deviation.code)
            let displayValue;
            if (config.fieldMappings && config.fieldMappings[field.table.displayField]) {
              // Use fieldMappings path (e.g., 'riskAssessment.code' -> displayFieldValue.code)
              const mappingPath = config.fieldMappings[field.table.displayField];
              const pathParts = mappingPath.split('.');
              if (pathParts.length > 1 && pathParts[0] === field.table.displayField) {
                // The mapping is like 'riskAssessment.code', so get the code property
                displayValue = displayFieldValue[pathParts[1]];
              } else {
                // Fallback: try to extract from the mapping path
                displayValue = displayFieldValue.code || displayFieldValue.name || 
                               displayFieldValue.typeName || 
                               displayFieldValue.statusName || 
                               displayFieldValue.title || 
                               displayFieldValue.version;
              }
            } else {
              // Default: prefer code if available, then fall back to other fields
              displayValue = displayFieldValue.code || 
                           displayFieldValue.name || 
                               displayFieldValue.typeName || 
                               displayFieldValue.statusName || 
                               displayFieldValue.title || 
                               displayFieldValue.version ||
                               `${field.table.displayField} #${displayFieldValue.id || 'Unknown'}`;
            }
            
            enhanced[field.table.displayField] = displayValue;
            
            // Debug logging for first entity to verify mapping
            if (entities.indexOf(entity) === 0) {
              // Conversion completed
            }
            }
          }
          
          // Also handle lookup endpoint mapping for fields with both displayField and lookupEndpoint
          if (field.lookupEndpoint) {
            const sourceField = field.name.replace('Id', ''); // Remove 'Id' suffix
            
            // Handle nested object properties from related fields
            if (entity[sourceField] && typeof entity[sourceField] === 'object' && !Array.isArray(entity[sourceField])) {
              // Check for user properties first (for user lookup endpoints)
              let displayValue = entity[sourceField].userDisplayName || 
                               (entity[sourceField].userFirstName && entity[sourceField].userLastName 
                                 ? `${entity[sourceField].userFirstName} ${entity[sourceField].userLastName}`.trim() 
                                 : null) ||
                               entity[sourceField].name ||
                               entity[sourceField].catName ||
                               entity[sourceField].catCode ||
                               entity[sourceField].typeName || 
                               entity[sourceField].statusName || 
                               entity[sourceField].title ||
                               entity[sourceField].userEmail ||
                               entity[sourceField].email ||
                               null;
              
              // Only set to 'Unknown' if we truly have no value, otherwise preserve the object
              const hasFieldMapping =
                config.fieldMappings && config.fieldMappings[field.table.displayField];
              if (
                displayValue === null &&
                field.table.displayField === sourceField &&
                !hasFieldMapping
              ) {
                // For user fields, preserve the object instead of converting to 'Unknown'
                // This allows the details view to access the full user object
                enhanced[field.table.displayField] = entity[sourceField];
              } else if (displayValue !== null) {
                enhanced[field.table.displayField] = displayValue;
              }
              
              // Debug logging for first entity to verify mapping
              if (entities.indexOf(entity) === 0) {
                // Lookup mapping completed
              }
            }
          }
        }
      });

      if (typeof config.enrichEntity === 'function') {
        return config.enrichEntity(enhanced);
      }

      return enhanced;
    });
  }, [scopedEntities, config.fields, config.fieldMappings, config.enrichEntity]);

  // Ensure table columns are properly set up (rebuild from fields when needed)
  const ensuredTableColumns = useMemo(() => {
    const fromConfig =
      config.tableColumns && config.tableColumns.length > 0
        ? config.tableColumns
        : null;

    let columns = fromConfig
      ? [...fromConfig]
      : (config.fields || [])
          .filter((field) => field.table !== false && field.table)
          .map((field) => ({
            key: field.table.displayField || field.name,
            header: field.table.header || field.label,
            width: field.table.width || 'auto',
            sortable: field.table.sortable !== false,
            displayField: field.table.displayField || field.name,
            cellRenderer: field.table.cellRenderer || 'default',
            dateFormat: field.table.dateFormat,
            originalField: field.name,
            maxLines: field.table.maxLines,
            maxLength: field.table.maxLength,
          }));

    if (!columns.length) {
      columns = [
        { key: 'title', header: 'Title', width: '300px' },
        { key: 'code', header: 'Code', width: '120px' },
        { key: 'createdAt', header: 'Created', width: tableConfig.dateColumnWidth },
        { key: 'updatedAt', header: 'Updated', width: tableConfig.dateColumnWidth },
      ];
    }

    // Always show Document Reference as the last data column
    const referenceIdx = columns.findIndex(
      (col) =>
        col.key === 'referenceDocument' || col.originalField === 'referenceDocument',
    );
    if (referenceIdx >= 0 && referenceIdx !== columns.length - 1) {
      const [referenceCol] = columns.splice(referenceIdx, 1);
      columns.push(referenceCol);
    }

    return columns;
  }, [config.tableColumns, config.fields]);

  // Prepare lookup options for form fields
  const enhancedFields = useMemo(() => {
    return config.fields?.map(field => {
      if (field.lookupEndpoint && (field.type === 'select' || field.type === 'multiSelect')) {
        const lookupEndpoint = field.lookupEndpoint;
        const rawLookupData = memoizedLookupQueries[lookupEndpoint] || [];
        const lookupData =
          typeof field.lookupFilter === 'function'
            ? rawLookupData.filter((item) => field.lookupFilter(item))
            : rawLookupData;
        
        // Convert lookup data to options format - ensure proper structure
        const options = Array.isArray(lookupData) && lookupData.length > 0 
          ? lookupData.map(item => {
              // Ensure we have a valid item with an id
              if (!item || typeof item !== 'object' || !item.id) {
                return null;
              }
              
              // Use custom option label formatter first, then labelField, then fallbacks
              let labelValue;
              if (typeof field.optionLabel === 'function') {
                labelValue = field.optionLabel(item);
              } else if (field.labelField && item[field.labelField] && String(item[field.labelField]).trim()) {
                labelValue = item[field.labelField];
              } else {
                labelValue =
                  item.name ||
                  item.catName ||
                  item.typeName ||
                  item.statusName ||
                  item.title ||
                  item.userDisplayName ||
                  `Item ${item.id}`;
              }
              
              return {
                value: item.id,
                label: String(labelValue)
              };
            }).filter(Boolean) // Remove any null items
          : [];

        const enhanced = {
          ...field,
          options: options
        };

        // Resolve locked lookup (e.g. Procedures module always = PROCEDURES)
        if (field.lockedLookupName) {
          const lockedItem = (Array.isArray(rawLookupData) ? rawLookupData : []).find(
            (item) =>
              String(item?.name || '').toUpperCase() ===
              String(field.lockedLookupName).toUpperCase()
          );
          if (lockedItem?.id != null) {
            enhanced.defaultValue = lockedItem.id;
            enhanced.lockedValue = lockedItem.id;
            enhanced.options = options.filter(
              (opt) => Number(opt.value) === Number(lockedItem.id)
            );
          }
        }
        
        return enhanced;
      }
      return field;
    }) || [];
  }, [config.fields, memoizedLookupQueries]);

  // Handle error state after all hooks
  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          Error loading {config.entityName}: {error.message}
        </div>
      </div>
    );
  }

  // Optional: Debug logging (can be removed in production)
  if (process.env.NODE_ENV === 'development' && enhancedEntities.length > 0) {
    const firstEntity = enhancedEntities[0];
    const lookupDebugInfo = {};
    
    // Dynamically build debug info based on available lookup endpoints
    Object.keys(lookupEndpoints).forEach(key => {
      const sourceField = key.replace(/s$/, ''); // Remove plural 's' (e.g., 'documentTypes' -> 'documentType')
      if (firstEntity[sourceField]) {
        lookupDebugInfo[sourceField] = firstEntity[sourceField];
      }
    });
    

  }


  
  // Debug lookup data
  if (Object.keys(memoizedLookupQueries).length > 0) {

    
    // Debug: Check for any problematic option structures
    enhancedFields.filter(f => f.lookupEndpoint).forEach(field => {
      if (field.options && field.options.length > 0) {
        field.options.forEach((option, index) => {
          if (typeof option !== 'object' || !option.hasOwnProperty('value') || !option.hasOwnProperty('label')) {
            console.error(`❌ Invalid option structure in ${field.name}[${index}]:`, option);
          }
          if (typeof option.label !== 'string') {
            console.error(`❌ Non-string label in ${field.name}[${index}]:`, option.label);
          }
        });
      }
    });
  }

  // API-based save handler
  const handleSave = async (formData, formMode, currentData, setData) => {
    try {
      // STEP 1: Validate data before sending to API
      if (config.validateData && typeof config.validateData === 'function') {

        const validationResult = await config.validateData(formData);
        
        if (!validationResult.isValid) {

          const errorMessage = Object.values(validationResult.errors).join(', ');
          throw new Error(`Validation failed: ${errorMessage}`);
        }

      }
      
      // STEP 2: Remove system fields that shouldn't be sent to API
      const systemFieldsToRemove = [
        'id',
        'createdAt',
        'updatedAt',
        '_count',
        // Computed in the UI layer for tables; not a writable API field (breaks forbidNonWhitelisted DTOs)
        'fileAttachmentCount',
      ];
      
      // Allow certain fields to be kept based on config
      const fieldsToKeep = config.fieldsToKeep || [];
      const filteredSystemFieldsToRemove = systemFieldsToRemove.filter(field => !fieldsToKeep.includes(field));
      
      // Dynamically add relation fields based on lookup endpoints
      const relationFieldsToRemove = [];
      Object.keys(lookupEndpoints).forEach(key => {
        // Convert plural endpoint names to singular field names
        // e.g., 'documentTypes' -> 'documentType', 'departments' -> 'department'
        const fieldName = key.replace(/s$/, '');
        relationFieldsToRemove.push(fieldName);
      });
      
      // Combine with config-specific fields to remove
      const fieldsToRemove = [...filteredSystemFieldsToRemove, ...relationFieldsToRemove, ...(config.fieldsToRemove || [])];
      
      let cleanedData = { ...formData };
      
      // Remove explicitly listed fields
      fieldsToRemove.forEach(field => {
        if (cleanedData.hasOwnProperty(field)) {
          delete cleanedData[field];

        }
      });
      
      // Also remove any fields that are objects (likely relations from API) - but preserve arrays
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] && 
            typeof cleanedData[key] === 'object' && 
            !Array.isArray(cleanedData[key]) && 
            cleanedData[key].constructor === Object) {
          delete cleanedData[key];

        }
      });

      // Convert string IDs to numbers for fields ending with "Id"
      // Normalize ID fields: send null instead of removing when cleared
      Object.keys(cleanedData).forEach(key => {
        if (key.endsWith('Id')) {
          if (cleanedData[key] === '' || cleanedData[key] === null || cleanedData[key] === undefined) {
            // Explicitly send null so backend can clear existing relation
            cleanedData[key] = null;
          } else {
            // Convert valid string IDs to numbers
            const numValue = Number(cleanedData[key]);
            if (!isNaN(numValue)) {
              cleanedData[key] = numValue;

            }
          }
        }
      });

      // Apply custom data transformation if defined in config
      if (config.transformData && typeof config.transformData === 'function') {
        // Replace cleanedData with transformed data (don't merge, as transformData should return a clean object)
        cleanedData = config.transformData(cleanedData, formMode);
      }

      // Debug: Log the final data being sent to API


      let savedEntity;
      if (formMode === 'add') {
        // Create new entity
        savedEntity = await createEntity(cleanedData);

      } else {
        // Update existing entity - use the standard pattern for all modules
        const entityId = formData.id;

        if (!entityId) {
          throw new Error(`Missing entity ID for update of ${config.entityName}.`);
        }

        savedEntity = await updateEntity(entityId, cleanedData);
      }

      // Refresh the data using SWR's mutate function
      await mutate();
      
      return savedEntity;
    } catch (error) {
      console.error('❌ Save error:', error);
      throw error; // Re-throw so GenericFormsPage can handle the error display
    }
  };

  // API-based delete handler
  const handleDelete = async (entity) => {
    try {
      await deleteEntity(entity.id);

      
      // Refresh the data using SWR's mutate function
      await mutate();
      
      return true;
    } catch (error) {
      console.error('❌ Delete error:', error);
      throw error; // Re-throw so GenericFormsPage can handle the error display
    }
  };

  // Pass the enhanced data and fields through config as expected by GenericFormsPage
  const configWithData = {
    ...config,
    data: enhancedEntities,
    fields: enhancedFields, // Use enhanced fields with populated options
    tableColumns: ensuredTableColumns,
    lookupData: memoizedLookupQueries, // Also pass raw lookup data for reference
    // Use custom onSave if provided, otherwise use default handleSave
    onSave: config.onSave || handleSave, // Custom onSave takes precedence
    // Use custom onDelete if provided, otherwise use default handleDelete
    onDelete: config.onDelete || handleDelete, // Custom onDelete takes precedence
    // Pass through customHandlers (e.g., onDuplicate)
    customHandlers: config.customHandlers || {}
  };

  return (
    <GenericFormsPage 
      config={configWithData}
    />
  );
};

export default GenericModulePage;